import React, { useEffect, useState } from 'react';
import './AdminEditUser.css';
import { Circles } from 'react-loader-spinner';
import privateAxios from '../../utils/axios/privateAxios';
import { useParams, useNavigate } from 'react-router-dom';
import Notification from '../../components/Notification/Notification';
import { villages, towns, educationLevels, occupations, stateOfResidence, roles } from '../../assets/exports.js';

const AdminEditUser = () => {
    const { id: userId } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ type: '', message: '' });

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification({ type: '', message: '' }), 5000);
    };

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const { data } = await privateAxios.get(`/private/admin-user/${userId}`);
                const user = data.user;

                const formattedDOB = user.dateOfBirth
                    ? new Date(user.dateOfBirth).toISOString().substring(0, 10)
                    : '';

                setFormData({
                    ...user,
                    dateOfBirth: formattedDOB,
                    verified: Boolean(user.verified),
                    roles: user.roles || [],
                });
            } catch (err) {
                console.error('Error fetching user data:', err);
                setError('Failed to load user data.');
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'verified') {
            setFormData(prev => ({ ...prev, [name]: value === 'true' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleRoleToggle = (roleName) => {
        setFormData(prev => {
            const updatedRoles = prev.roles.includes(roleName)
                ? prev.roles.filter(r => r !== roleName)
                : [...prev.roles, roleName];
            return { ...prev, roles: updatedRoles };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await privateAxios.patch(`/private/edit-user/${userId}`, formData);
            showNotification('success', 'User profile updated successfully.');
            setTimeout(() => {
                navigate('/admin-users');
            }, 3000);
        } catch (err) {
            console.error('Error updating user profile:', err);
            showNotification('error', 'Failed to update profile.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', columnGap: '10px', alignItems: 'center', justifyContent: 'center', height: '300px', color: 'gray' }}>
                <Circles height="50" width="50" color="#008751" ariaLabel="loading" /> <h3>Fetching user...</h3>
            </div>
        )
    };

    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className="admin-edit-user">
            <Notification
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification({ type: '', message: '' })}
            />
            <div className="admin-edit-user-container">
                <h2>Edit User Profile</h2>
                <form onSubmit={handleSubmit}>

                    {/* Username */}
                    <div className="form-group">
                        <label>Username</label>
                        <input
                            type="username"
                            name="username"
                            value={formData.username || ''}
                            onChange={handleChange}
                        />
                    </div>

                    {/* First name */}
                    <div className="form-group">
                        <label>First name</label>
                        <input
                            type="firstname"
                            name="firstname"
                            value={formData.firstname || ''}
                            onChange={handleChange}
                        />
                    </div>


                    {/* Last name */}
                    <div className="form-group">
                        <label>Last name</label>
                        <input
                            type="lastname"
                            name="lastname"
                            value={formData.lastname || ''}
                            onChange={handleChange}
                        />
                    </div>

                    {/* Email */}
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email || ''}
                            onChange={handleChange}
                        />
                    </div>

                    {/* Phone number */}
                    <div className="form-group">
                        <label>Phone number</label>
                        <input
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber || ''}
                            onChange={handleChange}
                        />
                    </div>


                    {/* Gender select */}
                    <div className="form-group">
                        <label>Gender</label>
                        <select name="gender" value={formData.gender || ''} onChange={handleChange}>
                            <option value="">Select gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    {/* Date of Birth */}
                    <div className="form-group">
                        <label>Date of Birth</label>
                        <input
                            type="date"
                            name="dateOfBirth"
                            value={formData.dateOfBirth || ''}
                            onChange={handleChange}
                        />
                    </div>

                    {/* Bio textarea */}
                    <div className="form-group">
                        <label>Bio</label>
                        <textarea
                            name="bio"
                            rows={4}
                            value={formData.bio || ''}
                            onChange={handleChange}
                        />
                    </div>


                    {/* Education Level select */}
                    <div className="form-group">
                        <label>Education Level</label>
                        <select
                            name="educationLevel"
                            value={formData.educationLevel || ''}
                            onChange={handleChange}
                        >
                            <option value="">Select education level</option>
                            {educationLevels.map(level => (
                                <option key={level} value={level}>{level}</option>
                            ))}
                        </select>
                    </div>

                    {/* Occupation select */}
                    <div className="form-group">
                        <label>Occupation</label>
                        <select
                            name="occupation"
                            value={formData.occupation || ''}
                            onChange={handleChange}
                        >
                            <option value="">Select occupation</option>
                            {occupations.map(job => (
                                <option key={job} value={job}>{job}</option>
                            ))}
                        </select>
                    </div>

                    {/* Village select */}
                    <div className="form-group">
                        <label>Village</label>
                        <select
                            name="village"
                            value={formData.village || ''}
                            onChange={handleChange}
                        >
                            <option value="">Select village</option>
                            {villages.map(v => (
                                <option key={v} value={v}>{v}</option>
                            ))}
                        </select>
                    </div>

                    {/* Town select */}
                    <div className="form-group">
                        <label>Town</label>
                        <select
                            name="town"
                            value={formData.town || ''}
                            onChange={handleChange}
                        >
                            <option value="">Select town</option>
                            {towns.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    {/* State select */}
                    <div className="form-group">
                        <label>State of Residence</label>
                        <select
                            name="stateOfResidence"
                            value={formData.stateOfResidence || ''}
                            onChange={handleChange}
                        >
                            <option value="">Select State of Residence</option>
                            {stateOfResidence.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    {/* Status dropdown */}
                    <div className="form-group">
                        <label>Status</label>
                        <select
                            name="status"
                            value={formData.status || ''}
                            onChange={handleChange}
                        >
                            <option value="">Select status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    {/* ROLES CHECKBOXES */}
                    <div className="form-group">
                        <label>Roles</label>
                        <div className="role-checkboxes">
                            {roles.map(role => (
                                <div className='admin-edit-user-roles' key={role}>
                                    <input
                                        type="checkbox"
                                        id='admin-edit-user-checkbox'
                                        checked={formData.roles?.includes(role)}
                                        onChange={() => handleRoleToggle(role)}
                                    />
                                    <div className='admin-edit-user-role'>{role}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Verified select */}
                    <div className="form-group">
                        <label>Verified</label>
                        <select
                            name="verified"
                            value={formData.verified ? 'true' : 'false'}
                            onChange={handleChange}
                        >
                            <option value="true">True</option>
                            <option value="false">False</option>
                        </select>
                    </div>

                    <button type="submit" className="submit-btn">{loading ? 'Saving Changes...' : 'Save Changes'}</button>
                </form>
            </div>
        </div>
    );
};

export default AdminEditUser;